package com.crowpay.views.screens.contractor

import com.crowpay.*
import com.crowpay.contractor
import com.crowpay.extensions.isBlank
import com.crowpay.extensions.isNotBlank
import com.crowpay.views.components.nav.AppDimensions
import com.crowpay.extensions.toFileData
import com.crowpay.sdk.notNullSession
import com.crowpay.utils.*
import com.crowpay.utils.validation.Validator
import com.crowpay.utils.validation.interceptWrite
import com.crowpay.utils.validation.validate
import com.crowpay.views.components.*
import com.crowpay.views.components.files.*
import com.crowpay.views.components.nav.Form
import com.crowpay.views.components.nav.viewIsNarrow
import com.crowpay.views.components.project.work.scope.EditLineItems
import com.crowpay.views.dialogs.CreateOrEditNote
import com.crowpay.views.dialogs.GenericConfirmationDialog
import com.crowpay.views.dialogs.GenericDialog
import com.crowpay.views.screens.common.LandingScreen
import com.crowpay.views.theming.*
import com.lightningkite.UUID
import com.lightningkite.kiteui.ConsoleRoot
import com.lightningkite.kiteui.QueryParameter
import com.lightningkite.kiteui.Routable
import com.lightningkite.kiteui.models.*
import com.lightningkite.kiteui.navigation.dialogScreenNavigator
import com.lightningkite.kiteui.navigation.render
import com.lightningkite.kiteui.navigation.screenNavigator
import com.lightningkite.kiteui.reactive.*
import com.lightningkite.kiteui.suspendCoroutineCancellable
import com.lightningkite.kiteui.views.*
import com.lightningkite.kiteui.views.direct.*
import com.lightningkite.kiteui.views.l2.icon
import com.lightningkite.lightningdb.*
import com.lightningkite.serialization.lensPath
import kotlinx.coroutines.*
import kotlinx.serialization.Serializable
import kotlin.coroutines.resume


@Routable("/contractor/project/{projectId}/edit")
class EditProject(val projectId: UUID) : ContractorPage, Form {
    @QueryParameter
    val selectedTab = Property(ProjectForm.Section.Details)

    private val form = ProjectForm(projectId, selectedTab)

    override val saved: Readable<Boolean> get() = form.saved

    override val title: Readable<String> get() = form.title

    override fun ViewWriter.renderMainContent() { form.run { render2() } }
}

class ProjectForm(
    val projectId: UUID,
    val selectedTab: Property<Section> = Property(Section.Billing),
) : ContractorPage, Form {
    @Serializable
    enum class Section(override val displayName: String) : HasDisplayName {
        Billing("Billing"), Info("Info"), Details("Details"), Scope("Scope"),
    }

    override val title: Readable<String> = Constant("Create Project")

    val inDatabase = shared { notNullSession().projects[projectId]() != null }

    val projectValidator = Validator()
    val addressValidator = Validator(projectValidator)

    val projectDraft = Draft {
        notNullSession().projects[projectId]() ?: Project(
            _id = projectId,
            contractor = notNullContractor(),
            name = "",
            description = "",
            address = Address(),
            location = "",
        )
    }

    val notes = shared {
        notNullSession().contractorNotes.query(
            Query(orderBy = sort { it.at.ascending() }) { it.project eq projectId }
        )()
    }

    val files = LazyProperty { projectDraft().files.toFileData() }

    val editLineItems = EditLineItems(
        projectId,
        inDatabase,
        onSave = { item ->
            if (!inDatabase()) {
                saveProject(false)
            }
        }
    )

    val totalPrice = shared {
        editLineItems.forms()
            .sumOf { it.draft().originalPrice }
    }

    val clients = shared {
        notNullSession().clients.query(
            Query(condition { it.contractor eq projectDraft().contractor })
        )()
    }

    val client = LazyProperty {
        val p = projectDraft().client ?: return@LazyProperty null
        val c = clients()
        c.find { it._id == p }
    }

    val addressSameAsClient = LazyProperty {
        val address = projectDraft.once().address
        address.isNotBlank() && address == client.once()?.address
    }

    val contractorNotInApprovedStates = shared {
        val relevantState = projectDraft.invoke().address.state
        val session = notNullSession()
        val licensedStates = session.licenses.query(
            Query(
                condition = condition {
                    it.contractor eq notNullContractor()
                }
            )
        )().map { it.state }
        val approvedStates = session.approvedStates.query(
            Query(
                condition { it.approved eq true }
            )
        )().map { it._id }
        relevantState !in approvedStates || relevantState !in licensedStates
    }


    override val saved: Readable<Boolean> = shared {
        inDatabase() && !projectDraft.changesMade() && editLineItems.saved()
    }

    suspend fun saveProject(includeLineItems: Boolean) {
        coroutineScope {
            projectDraft.modify {
                it.copy(
                    files = files().resolveProjectAttachments(),
                    client = client()?._id,
                )
            }
            projectDraft.pushChanges(notNullSession().projects)     // Handles both insertion and modification

            if (includeLineItems) {
                editLineItems.forms.awaitOnce().forEachAsync(5) { it.save.startAction(this) }
            }
        }
    }

    suspend fun ViewWriter.saveProject() {

        val includeLineItems = when {
            editLineItems.forms().isEmpty() -> false
            !inDatabase() -> true
            editLineItems.hasUnsavedChanges() -> suspendCoroutineCancellable temp@{ cont ->
                dialogScreenNavigator.navigate(
                    GenericConfirmationDialog(
                        header = "Save Work Items",
                        message = "You have unsaved changes in your work items. Do you wish to save those now as well?",
                        confirmationText = "Include Work Items",
                        declineText = "Only Project",
                        onSubmit = { includeWorkItems ->
                            cont.resume(includeWorkItems)
                        }
                    ))
                return@temp {
                    dialogScreenNavigator.dismiss()
                    cont.resume(false)
                }
            }

            else -> false
        }

        println("Include line items: $includeLineItems")

        saveProject(includeLineItems = includeLineItems)
    }

    private fun ViewWriter.actionBar(): ViewModifiable {
        fun ViewWriter.saveButton() = button {
            specCenteredText("Save")

            onClick {
                try {
                    saveProject()
                } catch (e: Exception) {
                    ConsoleRoot.error(e)
                    dialogScreenNavigator.navigate(GenericDialog("Failed to save project"))
                }
            }
        }

        fun ViewWriter.saveAndCloseButton() = secondaryButton - button {
            specCenteredText("Save & Close")

            onClick {
                try {
                    saveProject()
                    screenNavigator.reset(ContractorProjects())
                } catch (e: Exception) {
                    dialogScreenNavigator.navigate(GenericDialog("Failed to save project"))
                }
            }
        }

        fun ViewWriter.discardButton() = textButton - button {
            specCenteredText("Discard")
            onClick {
                dialogScreenNavigator.navigate(
                    GenericConfirmationDialog(
                        header = "Discard Changes?",
                        message = "You have changes that have not been submitted. Discarded changes cannot be recovered. Discard changes and leave?",
                        confirmationText = "Discard & Leave",
                        onSubmit = submit@{ confirmed ->
                            if (!confirmed) return@submit

                            screenNavigator.reset(ContractorProjects())
                        }
                    )
                )
            }
        }

        fun ViewWriter.deleteButton() = deleteButton - button {
            centered - icon(Icon.trash.copy(width = 1.5.rem, height = 1.5.rem), "Delete Project")
            onClick {
                dialogScreenNavigator.navigate(
                    GenericConfirmationDialog(
                        header = "Delete Draft?",
                        message = "You are about to delete this project draft. Deleted drafts cannot be recovered. Delete this draft?",
                        confirmationText = "Delete Draft",
                        confirmationTheme = DangerButtonSemantic,
                        onSubmit = { confirmed ->
                            if (confirmed) {
                                notNullSession().projects[projectId].delete()
                                screenNavigator.reset(ContractorProjects())

                            }
                        }
                    )
                )
            }
        }

        fun ViewWriter.publishButton() = primaryButton - button {
            specCenteredText("Publish")
            onClick {
                val p = projectDraft.awaitOnce()
                val f = files.awaitOnce()
                if (projectDraft.changesMade.awaitOnce() ||
                    p.files.size != f.size ||
                    files.awaitOnce().any { it.preview is FilePreview.LocalFile } ||
                    editLineItems.hasUnsavedChanges()
                ) {
                    val confirmed = suspendCoroutineCancellable { cont ->
                        dialogScreenNavigator.navigate(
                            GenericConfirmationDialog(
                                header = "Save Project?",
                                message = "This project has unsaved changes. You must save the these changes before you can publish.",
                                confirmationText = "Save Project",
                                onSubmit = { confirmed ->
                                    cont.resume(confirmed)
                                },
                            )
                        )
                        return@suspendCoroutineCancellable { dialogScreenNavigator.dismiss() }
                    }
                    if (confirmed) {
                        saveProject(includeLineItems = true)
                        delay(1000)
                    } else
                        return@onClick
                }

                val current = projectDraft()
                val projectIssues = listOfNotNull(
                    if (current.name.isBlank()) "Name is missing" else null,
                    if (current.client == null) "No client selected" else null,
                    if (current.retention?.let { it < 0 || it > 0.1 } == true) "Project retention must be between 0% and 10%." else null,
                    if (current.address.isBlank()) "Address is missing fields" else null,
                    if (current.description.isBlank()) "Description is blank" else null,
                )

                val scopeIssues = editLineItems.forms.awaitOnce().flatMap { it.issues() }.toSet()

                when {
                    editLineItems.forms.await().isEmpty() -> {
                        dialogScreenNavigator.navigate(
                            GenericDialog("You must include at least one Work Item in your project.")
                        )
                    }

                    contractorNotInApprovedStates() -> {
                        dialogScreenNavigator.navigate(
                            GenericDialog("Projects can only by published by contractors registered to operate in approved states.")
                        )
                    }

                    projectIssues.isEmpty() && scopeIssues.isEmpty() -> {
                        dialogScreenNavigator.navigate(
                            GenericConfirmationDialog(
                                header = "Publish Project?",
                                message = "Publishing a new project will make it available to view and be accepted by the homeowner. Please make sure all project details are correct before proceeding. Once the homeowner accepts, any changes will need mutual agreement.",
                                confirmationText = "Publish Project",
                                onSubmit = {
                                    if (it) {
                                        val session = notNullSession()
                                        session.nonCached.project.publishProject(projectId)
                                        delay(100)
                                        session.projects.totallyInvalidate()
                                        screenNavigator.reset(ContractorProject(projectId))
                                    }
                                }
                            )
                        )
                    }

                    else -> {
                        dialogScreenNavigator.navigate(
                            GenericDialog(
                                "Invalid Fields",
                                buildString {
                                    appendLine("There are some invalid fields on your project. Please fix the following issues and try again:")
                                    appendLine()

                                    if (projectIssues.isNotEmpty()) {
                                        appendLine("Project Issues")
                                        projectIssues.joinTo(this, separator = "\n - ", prefix = " - ")
                                    }

                                    if (projectIssues.isNotEmpty() && scopeIssues.isNotEmpty()) appendLine()

                                    if (scopeIssues.isNotEmpty()) {
                                        appendLine("Scope Issues")
                                        scopeIssues.joinTo(this, separator = "\n - ", prefix = " - ")
                                    }
                                }
                            )
                        )
                    }
                }
            }
        }

        return darkSection - stack {
            spacing = AppDimensions.sectionIndent / 2

            if (viewIsNarrow) row {
                spacing = AppDimensions.buttonRowSpacing
                deleteButton()
                expanding - space()
                secondaryButton - saveButton()
                publishButton()
            }
            else row {
                spacing = AppDimensions.buttonRowSpacing
                primaryButton - saveButton()
                saveAndCloseButton()
                discardButton()
                onlyWhen { inDatabase() } - deleteButton()

                expanding - space()

                publishButton()
            }
        }
    }

    private fun ViewWriter.projectInfo() = col {
        with(projectValidator) {
            requiredField("Project Name") {
                val value = projectDraft
                    .lensPath { it.name }
                    .validate { it.isNotBlank() }
                textInput {
                    hint = "Project Name"
                    content bind value
                }
            }

            field2("Job#/PO (Optional)") {
                textInput {
                    content bind projectDraft.lensPath { it.jobNumber }.nullToBlank()
                }
            }

            space()

            subTitle("Project Address")

            row {
                exists = false
                ::exists { client() != null }
                centered - checkbox { checked bind addressSameAsClient }
                centered - body("Same As Client Address")
                reactiveSuspending {
                    if (addressSameAsClient()) {
                        val billing = client()?.address
                        if (billing != null) {
                            projectDraft.set(
                                projectDraft().copy(
                                    address = billing
                                )
                            )
                        }
                    }
                }
            }

            onlyWhen { !addressSameAsClient() } - addressForm(
                projectDraft.lensPath { it.address },
                addressValidator,
                AppDimensions.wideCollapseWidth,
                forceValid = { addressSameAsClient() }
            )

//            body { ::content { "Address Valid: ${addressValidator.allValid()}" } }

            field2("Neighborhood/District (Optional)") {
                textInput {
                    hint = "Neighborhood/District"
                    content bind projectDraft.lensPath { it.location }.nullToBlank()
                }
            }
        }
        with(projectValidator) {
            val retention = projectDraft
                .lensPath { it.retention }
                .lens(
                    get = { it?.toDouble()?.times(100) },
                    set = { it?.toFloat()?.takeUnless { it == 0f }?.div(100) }
                )
                .validate {
                    if (it == null) true
                    else 0 <= it && it <= 10
                }

            field2("Retention (Percent)") {
                numberInput {
                    content bind retention
                    range = 0.0..10.0
                    hint = "0% to 10%"
                    keyboardHints = KeyboardHints.integer
                }
            }
        }
        col {
            label2("Total Price (Calculated from line items)") {
                body {
                    ::content{ totalPrice().renderDollars() }
                }
            }
            onlyWhen { projectDraft().retention != null } - col {
                label2("Total Retention") {
                    body {
                        ::content content@{
                            val t = totalPrice()
                            val r = projectDraft().retention ?: 0f
                            (t * r).toLong().renderDollars()
                        }
                    }
                }
            }
        }
    }

    private sealed class ClientSelection(override val displayName: String) : HasDisplayName {
        data object None : ClientSelection("Select Client")

        data object New : ClientSelection("+ New Client")

        data class Existing(val client: Client) : ClientSelection(
            client.name.takeIf { it.isNotBlank() }
                ?: client.email.takeIf { it.isNotBlank() }
                ?: client.phoneNumber.takeIf { it.isNotBlank() }
                ?.let { PhoneNumberFormat.USA.format(it.filter { it.isDigit() }) }
                ?: "Existing Client"
        )
    }

    private fun ViewWriter.billingInfo() = col {
        val clientSelection = LazyProperty {
            client()?.let(ClientSelection::Existing) ?: ClientSelection.None
        }.interceptWrite {
            if (it is ClientSelection.Existing) client.value = it.client
            value = it
        }

        requiredField("Client") {
            projectValidator.addCondition { client() != null }

            select {
                bind(
                    clientSelection,
                    shared {
                        listOf(
                            ClientSelection.None,
                            ClientSelection.New
                        ) + clients().map(ClientSelection::Existing)
                    }
                ) { it.displayName }
            }
        }

        onlyWhen { clientSelection() is ClientSelection.Existing } - col {
            keyString("Client Name") { client()?.name }
            keyString("Address") { client()?.address?.toString() }
            keyString("Email") { client()?.email }
            keyString("Phone") { client()?.phoneNumber?.let { PhoneNumberFormat.USA.format(it.filter { it.isDigit() }) } }
        }

        val clientForm = ClientForm(
            LazyProperty {
                Client(
                    contractor = projectDraft().contractor,
                    name = "",
                    email = "",
                    phoneNumber = "",
                    address = Address()
                )
            },
        ) { newClient, saved ->
            if (saved) {
                clientSelection set ClientSelection.Existing(newClient)
                client.value = newClient
            }
            else clientSelection set ClientSelection.None
        }

        onlyWhen { clientSelection() is ClientSelection.New } - clientForm.render(this)
    }

    private fun ViewWriter.projectDetailForm() {
        col {
            spacing = 1.rem
            with(projectValidator) {
                requiredField("Project Description") {
                    val description = projectDraft.lensPath { it.description }.validate { it.isNotBlank() }

                    sizeConstraints(minHeight = 7.rem) - expanding - textArea {
                        hint = "Project Description"
                        content bind description
                    }
                }

                label2("Contractor Notes (Visible only to you)") {
                    fieldTheme - sizeConstraints(minHeight = 10.rem) - row {
                        expanding - stack {
                            centered - bold - subTitle {
                                ::exists { notes().isEmpty() }
                                content = "No Notes"
                            }
                            scrollsHorizontally - row {
                                spacing = 1.25.rem
                                exists = false
                                ::exists { notes().isNotEmpty() }

                                forEachUpdating(notes, 0) { note ->
                                    val index = shared { notes().indexOf(note()) + 1 }
                                    sizeConstraints(maxWidth = 25.rem) - padded - col {
                                        spacing = 1.rem
                                        row {
                                            spacing = AppDimensions.cornerRadii
                                            col {
                                                spacing = AppDimensions.cornerRadii
                                                subTitle {
                                                    ::content { "Private Note ${index()}" }
                                                }
                                                centered - smallBody {
                                                    ::content { note().at.formatLong() }
                                                }
                                            }
                                            atTop - row {
                                                spacing = 0.5.rem
                                                space()
                                                buttonTheme - button {
                                                    themeChoice += ThemeDerivation { it.copy(outlineWidth = 0.px).withoutBack }
                                                    spacing = 1.dp
                                                    centered - icon(
                                                        Icon.edit.copy(width = 1.5.rem, height = 1.5.rem),
                                                        "Edit"
                                                    )
                                                    onClick {
                                                        dialogScreenNavigator.navigate(
                                                            CreateOrEditNote(
                                                                note(),
                                                                "Add notes, pictures, or files that are for INTERNAL team only.  (ie. client or property considerations for managers and crews.)"
                                                            )
                                                        )
                                                    }
                                                }
                                                deleteButton - button {
                                                    spacing = 1.dp
                                                    centered - icon(
                                                        Icon.trash.copy(width = 1.5.rem, height = 1.5.rem),
                                                        "Delete"
                                                    )
                                                    onClick {
                                                        dialogScreenNavigator.navigate(
                                                            GenericConfirmationDialog(
                                                                "Delete Note",
                                                                "Are you sure you want to delete this note?",
                                                                confirmationTheme = DangerButtonSemantic,
                                                                confirmationText = "Delete"
                                                            ) {
                                                                notNullSession().contractorNotes[note()._id].delete()
                                                            }
                                                        )
                                                    }
                                                }
                                            }
                                        }

                                        sizeConstraints(maxHeight = 20.rem) - body {
                                            wraps = true
                                            ellipsis = true
                                            ::content{ note().message }
                                        }

                                        stack {
                                            val f = note.lens { it.files }
                                            ::exists { f().isNotEmpty() }
                                            label2("Files: ") {
                                                renderFiles(
                                                    f,
                                                    AppDimensions.smallImagePreview
                                                )
                                            }
                                        }
                                    }
                                }
                            }
                        }
                        sizeConstraints(width = AppDimensions.largeImagePreview / 3) - tertiaryButton - button {
                            iconAndAction(Icon.add, "Add Note") {
                                dialogScreenNavigator.navigate(
                                    CreateOrEditNote(
                                        projectDraft()._id,
                                        null,
                                        subHeader = "Add notes, pictures, or files that are for INTERNAL team only.  (ie. client or property considerations for managers and crews.)"
                                    ) {
                                        saveProject(includeLineItems = false)
                                    }
                                )
                            }
                        }
                    }
                }

                label2("Project Files (Images, Bids, Plans, Specs)") {
                    fieldTheme - fileManager(
                        files,
                        "No Files Uploaded"
                    ) { notNullSession().nonCached.project::uploadFileForRequest }
                }
            }
        }
    }

    private fun ViewWriter.assertProjectContractor() = launch {
        val session = notNullSession.await()
        val p = projectDraft.published()
        if (selectedContractor.await() != p.contractor) {
            val self = session.self.await()
            if (self.membership != null && self.membership?.contractor == p.contractor)
                selectedContractor.set(p.contractor)
            else
                screenNavigator.reset(LandingScreen())
        }
    }

    private fun ViewWriter.renderOld(): ViewModifiable {
        val infoExpanded = Property(true)
        val billingInfoExpanded = Property(true)

        return scrolls - col {
            spacing = 0.dp

            assertProjectContractor()

            sizeConstraints(width = AppDimensions.pageWidth) - col {
                spacing = AppDimensions.majorColumnSpacing

                title("Create Project")

                actionBar()

                onlyWhen { inDatabase() && contractorNotInApprovedStates() } - col {
                    notificationBar(AppColors.yellow) - body("Projects can only by published by contractors registered to operate in approved states.")
                    space()
                }

                rowCollapsingToColumn(AppDimensions.normalCollapseWidth) {
                    expanding - col {
                        spacing = 0.px
                        leftExpandButton(infoExpanded, highlightOnExpand = false) {
                            title("Project Information")
                        }
                        greySeparator()
                        onlyWhen { infoExpanded() } - projectInfo()
                    }
                    expanding - col {
                        spacing = 0.px
                        leftExpandButton(billingInfoExpanded, highlightOnExpand = false) {
                            title("Billing Information")
                        }
                        greySeparator()
                        onlyWhen { billingInfoExpanded() } - billingInfo()
                    }
                }

                workTabs - row {
                    expanding - buttonTheme - radioToggleButton {
                        checked bind selectedTab.equalTo(Section.Details)
                        body {
                            align = Align.Center
                            content = "Details"
                        }
                    }
                    expanding - buttonTheme - radioToggleButton {
                        checked bind selectedTab.equalTo(Section.Scope)
                        body {
                            align = Align.Center
                            content = "Scope"
                        }
                    }
                }

                col {
                    sizeConstraints(minHeight = 10.rem) - swapView {
                        swapping(
                            current = { selectedTab() },
                            views = { tab ->
                                when (tab) {
                                    Section.Details -> projectDetailForm()
                                    Section.Scope -> editLineItems.render(this@swapView)
                                    Section.Info,
                                    Section.Billing -> {
                                        selectedTab.value = Section.Details
                                        projectDetailForm()
                                    }
                                }
                            }
                        )
                    }
                    space(2.0)
                }

                onlyWhen { inDatabase() && contractorNotInApprovedStates() } - col {
                    notificationBar(AppColors.yellow) - body("Projects can only by published by contractors registered to operate in approved states.")
                    space()
                }

                actionBar()
            }
        }
    }

    private fun ViewWriter.renderNarrow() = col {
        expanding - scrolls - col {
            spacing = AppDimensions.majorColumnSpacing

            assertProjectContractor()

            title("Create Project")

            onlyWhen { inDatabase() && contractorNotInApprovedStates() } - notificationBar(AppColors.yellow) -
                    body("Projects can only by published by contractors registered to operate in approved states.")

            InnerTabBar.onNext - row {
                for (section in Section.entries) {
                    weight(1f + (0.1f * (section.displayName.length - 5))) - buttonTheme - radioToggleButton {
                        checked bind selectedTab.equalTo(section)
                        centered - body(section.displayName)
                    }
                }
            }

            stack {
                reactive {
                    clearChildren()
                    when (selectedTab()) {
                        Section.Info -> projectInfo()
                        Section.Details -> projectDetailForm()
                        Section.Billing -> billingInfo()
                        Section.Scope -> editLineItems.render(this@stack)
                    }
                }
            }
        }

        actionBar()
    }

    override fun ViewWriter.renderMainContent() {
        if (viewIsNarrow) renderNarrow()
        else renderOld()
    }
}